import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { CtBatchStoreService } from './ct-batch-store.service';
import { PersistDataService } from './persist-data.service';
import { catchError, filter, firstValueFrom, map, Observable, of, switchMap } from 'rxjs';

import {
  AppView,
  AppViewType,
  AuftragAssignmentView,
  ErfassungBehandlerView,
  ExtractionView,
  isConsolidationView,
  isDocumentView,
  isExtractionView,
  TableRowClassificationView,
} from '../../models/app-view';
import {
  createDocumentDataFieldGroup,
  ct2ConfigurationByBatchDomain,
  FieldModelKey,
} from '../../models/extraction-models/extraction-models';
import {
  CtBatch,
  CtDocument,
  DocumentClassKey,
  isEingangCtBatch,
  isProofCtBatch,
  isTaskCtBatch,
} from '@ps-gui/claims-terminal-shared';
import { entries, mapValues } from '@ps-gui/shared-utils';
import {
  ExtractionConfiguration,
  ExtractionPage,
  ExtractionSectionViewType,
  isTableModel,
} from '../../models/domain-extraction-specifications';
import { PageNavigationService } from './page-navigation.service';
import { ExtractedDataStoreService } from '../extracted-data/extracted-data-store.service';
import { createDialogHook } from '../workflow-hooks/common-hooks';
import { ExtractionCompletionDialogComponent } from '../../domains/dialogs/extraction-completion-dialog.component';
import { DialogService } from 'primeng/dynamicdialog';
import { SkipCurrentDocumentDialogComponent } from '../../domains/dialogs/skip-current-document-dialog.component';
import {
  FeatureFlagService,
  GUTACHTEN_AUFTRAGSARTEN_STRINGS,
  ToastService,
} from '@ps-gui/shared-core';
import { AuditCtBatchTransmissionService } from '../transmission/audit-ct-batch-transmission.service';
import { EingangCtBatchTransmissionService } from '../transmission/eingang-ct-batch-transmission.service';
import { TaskCtBatchTransmissionService } from '../transmission/task-ct-batch-transmission.service';
import { BatchLockGuardService } from '../batch-lock-guard.service';
import { PsServerCtBatchTransmissionService } from '../transmission/ps-server-ct-batch-transmission.service';
import {
  allowedDocumentClassesPerAuftragsart,
  documentClassesWithDedicatedForms,
} from './workflow-document-class-config';
import { ExtractedValue } from '../../models/extracted-values';

@Injectable({
  providedIn: 'root',
})
export class WorkflowService {
  private readonly ctBatchStoreService = inject(CtBatchStoreService);
  private readonly extractedDataStoreService = inject(ExtractedDataStoreService);
  private readonly ctBatchConfiguration =
    ct2ConfigurationByBatchDomain[this.ctBatchStoreService.ctBatchDomain];
  private readonly workflowHookService = inject(this.ctBatchConfiguration.workflowHookService);
  private readonly toastService = inject(ToastService);
  private readonly persistDataService = inject(PersistDataService);
  private readonly pageNavigationService = inject(PageNavigationService);
  private readonly dialogService = inject(DialogService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly auditTransmission = inject(AuditCtBatchTransmissionService);
  private readonly eingangTransmission = inject(EingangCtBatchTransmissionService);
  private readonly taskCtBatchTransmissionService = inject(TaskCtBatchTransmissionService);
  private readonly batchLockGuard = inject(BatchLockGuardService);
  private readonly psServerTransmission = inject(PsServerCtBatchTransmissionService);

  private views: AppView[] = [];

  private readonly currentViewIndex = signal(this.persistDataService.currentViewIndex ?? 0);

  readonly currentView = computed(() => this.views[this.currentViewIndex()]);
  readonly isFirstView = computed(() => this.currentViewIndex() === 0);

  constructor() {
    effect(() => {
      this.persistDataService.currentViewIndex = this.currentViewIndex();
    });

    effect(() => {
      if (this.isFirstView()) {
        this.pageNavigationService.disableNavigation('back');
      } else {
        this.pageNavigationService.enableNavigation('back');
      }
    });

    this.pageNavigationService.navigate$('skip').subscribe(() => this.skipCurrentDocument());
  }

  async init(): Promise<void> {
    this.views = [
      ...this.createAuftragAssignmentViews(),
      ...this.createErfassungBehandlerViews(),
      ...this.createExtractionViews(),
      ...this.createConsolidationViews(),
    ];

    await this.navigateToView(this.persistDataService.currentViewIndex ?? 0);
  }

  private createAuftragAssignmentViews(): AuftragAssignmentView[] {
    if (isTaskCtBatch(this.ctBatchStoreService.ctBatch)) {
      return [];
    }

    return [
      {
        type: AppViewType.AUFTRAG_ASSIGNMENT,
        shouldBeSkipped: async () => false,
      },
    ];
  }

  private createErfassungBehandlerViews(): ErfassungBehandlerView[] {
    if (!this.featureFlagService.configData().erfassungBehandlerGutachtenEnabled) {
      return [];
    }

    return [
      {
        type: AppViewType.ERFASSUNG_BEHANDLER,
        shouldBeSkipped: async () => {
          const auftragsart = await this.getSelectedAuftragsart();

          if (!auftragsart) {
            return true;
          }

          return !GUTACHTEN_AUFTRAGSARTEN_STRINGS.has(auftragsart.toString());
        },
      },
    ];
  }

  private createExtractionViews(): AppView[] {
    const documentsByClass = this.getDocumentsByClass();

    const extractCreationDateViews = this.ctBatchStoreService.ctBatch.documents
      .filter(
        (d) =>
          !documentClassesWithDedicatedForms.has(d.documentClasses[0]) &&
          d.documentClasses[0] !== '25_Q3_SONSTIGES',
      )
      .map((d) => this.createExtractDocumentCreationDateView(d, d.documentClasses[0]));

    return [
      ...extractCreationDateViews,
      ...entries<DocumentClassKey, ExtractionConfiguration>(
        this.ctBatchConfiguration.extractionConfigurationByClass,
      ).flatMap(([documentClass, { extractionPages }]) => {
        const documents = documentsByClass[documentClass] ?? [];
        return documents.flatMap((document) =>
          extractionPages.flatMap((page) =>
            this.createExtractionAppViews(document, page, documentClass),
          ),
        );
      }),
    ];
  }

  private createConsolidationViews(): AppView[] {
    const ctBatch = this.ctBatchStoreService.ctBatch;

    if (isTaskCtBatch(ctBatch)) {
      return [];
    }

    const splitInducingClassDocuments = ctBatch.documents.filter((document) =>
      document.documentClasses.includes(
        this.ctBatchConfiguration.batchSplitConfiguration?.splitInducingDocumentClass,
      ),
    );

    if (splitInducingClassDocuments.length === 0) {
      return [
        {
          splitDocumentId: undefined,
          splitEntityIndex: 0,
          type: AppViewType.CONSOLIDATION,
          shouldBeSkipped: async () => false,
        },
      ];
    }

    return splitInducingClassDocuments.map((document, index) => ({
      splitDocumentId: document.id,
      splitEntityIndex: index,
      type: AppViewType.CONSOLIDATION,
      shouldBeSkipped: async () => false,
    }));
  }

  /**
   * Creates a map of document classes to documents associated with this class, where by "associated"
   * we mean that a document contains this class ins its document class list.
   * Especially, the resulting map contains each document n times in total, where n is the number
   * of document classes of this document.
   */
  private getDocumentsByClass() {
    const ctBatch = this.ctBatchStoreService.ctBatch;
    return mapValues(
      Object.groupBy(
        this.ctBatchStoreService.ctBatch.documents
          .filter((document) => this.shouldDocumentExtract(document, ctBatch))
          .flatMap((document) =>
            document.documentClasses.map((documentClass) => [documentClass, document] as const),
          ),
        ([documentClass, _]) => documentClass,
      ),
      (_, value) => value.map(([_, document]) => document),
    );
  }

  private shouldDocumentExtract(document: CtDocument, ctBatch: CtBatch): boolean {
    if (!isProofCtBatch(ctBatch)) {
      return true;
    }
    return (
      !ctBatch.receivedAfterSubmission ||
      document.documentClasses.some((dc) =>
        this.ctBatchConfiguration.extractionDocumentClassesReceivedAfterSubmission.includes(dc),
      )
    );
  }

  private createExtractionAppViews(
    document: CtDocument,
    page: ExtractionPage,
    documentClass: DocumentClassKey,
  ): AppView[] {
    const extractionView: ExtractionView = {
      documentId: document.id,
      page: page,
      documentClass,
      shouldBeSkipped: async () => {
        const auftragsart = await this.getSelectedAuftragsart();

        if (!auftragsart) {
          return false;
        }

        return !allowedDocumentClassesPerAuftragsart[auftragsart].has(documentClass);
      },
      type: AppViewType.EXTRACTION,
    };

    const tableRowClassificationViews: TableRowClassificationView[] = page.sections
      .filter((section) => isTableModel(section))
      .filter(
        (section) =>
          section.rowClassification ||
          section.fieldModels.some((field) => field.inlineConsolidatorForField),
      )
      .map(
        (tableModel) =>
          ({
            documentClass,
            documentId: document.id,
            tableModel,
            shouldBeSkipped: async () =>
              !(await this.extractedDataStoreService.doTableRowsExist(document.id, tableModel.key)),
            type: AppViewType.TABLE_ROW_CLASSIFICATION,
          }) satisfies TableRowClassificationView,
      );

    return [extractionView, ...tableRowClassificationViews];
  }

  private createExtractDocumentCreationDateView(
    document: CtDocument,
    documentClass: DocumentClassKey,
  ): ExtractionView {
    return {
      documentId: document.id,
      page: {
        sections: [createDocumentDataFieldGroup()],
        viewType: ExtractionSectionViewType.SCROLLABLE,
      },
      documentClass,
      shouldBeSkipped: async () => {
        const auftragsart = await this.getSelectedAuftragsart();

        if (!auftragsart) {
          return true;
        }

        return !GUTACHTEN_AUFTRAGSARTEN_STRINGS.has(auftragsart.toString());
      },
      type: AppViewType.EXTRACTION,
    };
  }

  private async getSelectedAuftragsart(): Promise<ExtractedValue> {
    const snapshot = await this.persistDataService.getConsolidatedDataSnapshot(0);
    return snapshot.fields['auftragsart'];
  }

  transmitDataAndRedirect(): Observable<unknown> {
    return this.batchLockGuard
      .assureStillAssignedToCurrentUser(this.ctBatchStoreService.ctBatch)
      .pipe(switchMap(() => this.transmitData()));
  }

  private transmitData(): Observable<unknown> {
    const ctBatch = this.ctBatchStoreService.ctBatch;
    if (isProofCtBatch(ctBatch)) return this.auditTransmission.transmitAndRedirect(ctBatch);
    if (isEingangCtBatch(ctBatch)) {
      return this.featureFlagService.configData().psBackendWeiterleitungEnabled
        ? this.psServerTransmission.transmitAndRedirect(ctBatch)
        : this.eingangTransmission.transmitAndRedirect(ctBatch);
    }
    if (isTaskCtBatch(ctBatch))
      return this.taskCtBatchTransmissionService.transmitAndRedirect(ctBatch);
    throw new Error('Unsupported CtBatch type detected!');
  }

  /** Resolves `false` when the batch is no longer ours; the guard has shown the dialog by then. */
  private stillAssignedToCurrentUser(): Promise<boolean> {
    return firstValueFrom(
      this.batchLockGuard.assureStillAssignedToCurrentUser(this.ctBatchStoreService.ctBatch).pipe(
        map(() => true),
        catchError(() => of(false)),
      ),
    );
  }

  async navigateToPrevView() {
    const firstPreviousEnabledViewIndex = await this.getFirstPreviousEnabledViewIndex();
    if (firstPreviousEnabledViewIndex > -1) {
      await this.navigateToView(firstPreviousEnabledViewIndex);
    }
  }

  async skipCurrentDocument() {
    const currentView = this.currentView();
    if (!isDocumentView(currentView)) return;

    createDialogHook<boolean>(SkipCurrentDocumentDialogComponent)(this.dialogService)
      .pipe(
        filter((result) => !!result),
        switchMap(() =>
          this.extractedDataStoreService.deleteExtractedDataForDocument(
            currentView.documentId,
            this.ctBatchStoreService.ctBatchDomain,
            currentView.documentClass,
          ),
        ),
        switchMap(() => this.getNextEnabledViewNotForCurrentDocumentAndClass()),
      )
      .subscribe((nextViewIndex) => this.navigateToView(nextViewIndex, true));
  }

  async navigateToNextView() {
    // Check before committing to the next step, mirroring ct1's goToNextStep.
    if (!(await this.stillAssignedToCurrentUser())) {
      return;
    }

    const firstEnabledViewIndex = await this.getNextEnabledViewIndex();
    if (firstEnabledViewIndex > -1) {
      await this.navigateToView(firstEnabledViewIndex);
    } else {
      createDialogHook<boolean>(ExtractionCompletionDialogComponent)(this.dialogService)
        .pipe(
          filter((result) => !!result),
          switchMap(() =>
            this.toastService.runWithToast(
              this.transmitDataAndRedirect(),
              'Erfassung wird abgeschlossen',
              'Erfassung abgeschlossen',
              'Fehler beim Abschluss',
            ),
          ),
        )
        .subscribe();
    }
  }

  // when the user clicks "skip document", we want to skip all views that are associated
  // with the same document id AND document class. Thus, if we have a document D classified as
  // X+Y and the user skips extraction of (D, X), extraction of (D, Y) should follow.
  // If the skipped document was the last extraction step, we want to continue to consolidation.
  private async getNextEnabledViewNotForCurrentDocumentAndClass(): Promise<number> {
    const currentView = this.currentView();

    if (!isDocumentView(currentView)) return this.currentViewIndex();

    for (let index = this.currentViewIndex() + 1; index < this.views.length; index++) {
      const view = this.views[index];
      const shouldBeSkipped = await view.shouldBeSkipped();
      if (
        !shouldBeSkipped &&
        index > this.currentViewIndex() &&
        (!isDocumentView(view) ||
          view.documentId !== currentView.documentId ||
          view.documentClass !== currentView.documentClass)
      ) {
        return index;
      }
    }

    return -1;
  }

  private async getFirstPreviousEnabledViewIndex(): Promise<number> {
    for (let i = this.currentViewIndex() - 1; i >= 0; i--) {
      if (!(await this.views[i].shouldBeSkipped())) return i;
    }
    return -1;
  }

  private async getNextEnabledViewIndex(): Promise<number> {
    for (let i = this.currentViewIndex() + 1; i < this.views.length; i++) {
      if (!(await this.views[i].shouldBeSkipped())) return i;
    }
    return -1;
  }

  private async navigateToView(index: number, skipAfterViewHook = false) {
    const currentView = this.currentView();
    const nextView = this.views[index];
    const ctBatch = this.ctBatchStoreService.ctBatch;

    // We only want to execute hooks when we navigate "forward".
    // Going back should never trigger hooks.
    if (index <= this.currentViewIndex()) {
      this.currentViewIndex.set(index);
      return;
    }

    (skipAfterViewHook
      ? of(true)
      : this.workflowHookService.afterView(currentView, nextView, ctBatch)
    )
      .pipe(
        filter(Boolean),
        switchMap(() => this.workflowHookService.beforeView(nextView, ctBatch)),
        filter(Boolean),
        switchMap(() => {
          return !isConsolidationView(currentView) && isConsolidationView(nextView)
            ? this.workflowHookService.beforeConsolidation(this.ctBatchStoreService.ctBatch)
            : of(true);
        }),
        filter(Boolean),
      )
      .subscribe(() => this.currentViewIndex.set(index));
  }

  /**
   * Navigates to the first view for the given documentId containing either
   * * a FieldGroup section with a field matching the given field key, or
   * * a TableModel section where at least one table field model matches the given field key.
   */
  async navigateToViewForFieldKey(documentId: string, fieldKey: FieldModelKey): Promise<void> {
    const index = this.views.findIndex(
      (view) =>
        isExtractionView(view) &&
        view.documentId === documentId &&
        view.page.sections.some((section) =>
          section.fieldModels.some((fieldModel) => fieldModel.key === fieldKey),
        ),
    );
    await this.navigateToView(index);
  }
}
